import React, {useEffect, useRef, useState} from 'react';
import { Template } from "../../../components/template/template.jsx";
import "../../../css/style-report.css";
import "../../../css/input-date-time.css";
import ReportUtils from "../../../utils/report-utils.js";
import axios from "axios";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import {Card} from "../../../components/card/card.jsx";
import Content from "./components/content.jsx";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import Filters from "./components/filters.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import {format} from "date-fns";
import {useQuery} from "@tanstack/react-query";
import bosService from "../../../services/web/bos-service.js";
import {translate} from "../../../components/translate/translate.jsx";

const API_URL = import.meta.env.VITE_API_URL;
const currentMonth = format(new Date(), "MM");
const currentYear = format(new Date(), 'yyyy');

const initialFilters = () => ({
    month: { label: translate(currentMonth), value: currentMonth },
    year: { label: currentYear, value: currentYear },
    type: { label: "Resumida", value: "resumed" },
    team: []
});

const filterLabels = {
    month: "Mês",
    year: "Ano",
    type: "Tipo de análise",
    team: "Equipe"
};

const CorporationActions = () => {
    const [loading, setLoading] = useState(false);

    const [collapsed, setCollapsed] = useState(false);
    const buttonRef = useRef(null);
    const [fixedTop, setFixedTop] = useState(null);

    const [filters, setFilters] = useState(initialFilters);
    const [appliedFilters, setAppliedFilters] = useState(initialFilters);
    const [reportData, setReportData] = useState(null);

    const resumedData =
        reportData?.dispatch_list?.length > 0 ||
        reportData?.encounter_list?.length > 0 ||
        reportData?.bos_list?.length > 0 ||
        reportData?.detention_list?.length > 0 ||
        reportData?.apprehension_list?.length > 0;

    const detailedData =
        (reportData?.dispatch_cards?.[0]?.total ?? 0) > 0 ||
        (reportData?.encounter_cards?.[0]?.total ?? 0) > 0 ||
        (reportData?.bos_cards?.[0]?.total_bos ?? 0) > 0 ||
        (reportData?.detention_cards?.[0]?.detention ?? 0) > 0 ||
        (reportData?.apprehension_cards?.[0]?.total_apprehension ?? 0) > 0;

    const hasData = appliedFilters.type.value === 'detailed' ? detailedData : resumedData;

    const [title] = useState("Ações da corporação");
    const [headerSelected, setHeaderSelected] = useState(-1);

    const { data: boName } = useQuery({
        queryKey: ['boName'],
        queryFn: () => bosService.getBoName(),
        initialData: 'BO'
    });

    useEffect(() => {
        if (!reportData) return;
        ReportUtils.appendFilters(ReportUtils.filters(filters, filterLabels));
    }, [reportData]);

    const apply = async () => {
        ReportUtils.applyLoad({
            setLoading,
            clearContent: () => {
                setReportData(null)
            }
        });

        try {

            const formattedFilters = {
                month: filters.month.value ?? null,
                year: filters.year.value ?? null,
                type: filters.type.value ?? null,
                team: filters.team?.map(i => i.value) ?? []
            };

            const auditFilters = ReportUtils.filters(filters, filterLabels, true);

            const { data: response } = await axios.post(API_URL + "/corporation_actions/apply", {
                ...formattedFilters,
                filters: auditFilters
            });

            const data = response.data;
            setReportData(data);
            setAppliedFilters(filters);

            const scale = window.visualViewport?.scale ?? 1;
            if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

        } catch (error) {

            ReportUtils.alertErrorMessage("Erro ao gerar o relatório.");
            console.error("Erro ao gerar o relatório", error);

        } finally {
            setLoading(false);
        }
    };

    useGlobalKey("Enter", apply);

    const clearSearch = () => {
        setFilters(initialFilters);
        setReportData(null);
    }

    const toggleFilters = () => {
        if (!collapsed && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFixedTop(rect.top);
        }

        setCollapsed(prev => !prev);
    }

    const printComponentRef = useRef(null);

    return (
        <div className={"flex"}>

            <Template isLoading={loading}>
                <Template.Header>
                    <div className="flex justify-between w-full">
                        <Template.Title>{title}</Template.Title>
                        <HeaderButtons
                            exportable={true}
                            exportInSheets={true}
                            printable={true}
                            headerChange={true}
                            printComponentRef={printComponentRef}
                            reportData={reportData}
                            data={hasData}
                            headerSelected={headerSelected}
                            setHeaderSelected={setHeaderSelected}
                            setCollapsed={setCollapsed}
                        />
                    </div>
                </Template.Header>
                <Template.Body className={`${collapsed ? 'p-[0]' : ''}`}>
                    <Menu buttonRef={buttonRef} toggleFilters={toggleFilters} fixedTop={fixedTop}
                          collapsed={collapsed} apply={apply} clearSearch={clearSearch}>

                        <Filters setFilters={setFilters} filters={filters}/>
                        <AutoDatetime setFilters={setFilters}/>

                    </Menu>
                </Template.Body>
            </Template>

            <Card className={`w-full h-fit mr-2 ${!reportData || !hasData ? 'mt-[59px]' : 'mt-[61px]' } print:!mt-[-15px] transform ${!reportData || !hasData ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2' : ''}`}>
                <Card.Body>
                    <div className={`flex-1 ${!reportData || !hasData ? 'p-[0]' : 'px-[20px] py-[5px]'}`}>
                        <NotAppliedContent reportData={reportData}/>
                        <NoDataContent reportData={reportData} data={!hasData && 0}/>
                        <Content reportData={reportData} data={hasData} analysisType={appliedFilters.type.value} boName={boName} month={appliedFilters.month.label} year={appliedFilters.year.label} entity={headerSelected} title={title} collapsed={collapsed} setLoading={setLoading} printComponentRef={printComponentRef}/>
                    </div>
                </Card.Body>
            </Card>

        </div>
    );
};

export default CorporationActions;
